// @section 1 AccountFactsLens — captured facts about the account
//
// What we know about them: key/value rows in hq_bd.facts, FK'd to a specific
// deal. Card-grid layout grouped by inferred category (Profile / Location /
// People / Financial / Tech / Marketing). Quick-add chips suggest common keys
// the operator hasn't captured yet; inline composer for custom keys; click
// any card to edit value / source / source URL.
//
// Promoted to a top-level tab on 2026-05-27 — used to be a sub-tab inside the
// retired BD lens.

import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  listBdFacts, createBdFact, patchBdFact,
  type AccountAggregate, type AccountDeal, type BdFact,
} from '../../api';

export default function AccountFactsLens({ data }: { data: AccountAggregate }) {
  const companyId = data.company.id;
  const deals = data.deals;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {deals.length === 0 && <NoDealsBanner />}
      <FactsSection companyId={companyId} deals={deals} />
    </div>
  );
}

// @section 2 Category inference — keys group naturally into the operator's mental model
const CATEGORIES: { name: string; match: RegExp }[] = [
  { name: 'Profile',   match: /^(business[_-]?model|industry|product|service|description|founded|year_founded|headcount|company_size|employees|stage|tier|vertical|segment|niche|target_market|tagline|mission|vision|customer_count|years_in_business)/i },
  { name: 'Location',  match: /^(office|address|location|city|state|region|country|service_area|timezone|tz|hq|markets|territory|coverage)/i },
  { name: 'People',    match: /^(owner|ceo|founder|president|cfo|cto|coo|decision_maker|champion|primary_contact|key_contact|stakeholder|exec|leadership|reports_to)/i },
  { name: 'Financial', match: /^(revenue|annual_revenue|arr|mrr|funding|valuation|profit|margin|budget|spend|ebitda|gross_margin|cac|ltv|burn|runway|aum)/i },
  { name: 'Tech',      match: /^(stack|tech|software|systems?|integrations?|platform|crm|erp|primary_system|tools?|hosting|database|stack)/i },
  { name: 'Marketing', match: /^(website|domain|url|ga4|google_ads|seo|social|linkedin|facebook|instagram|twitter|youtube|x_com|brand|tagline|positioning|messaging|channels?|campaigns?)/i },
];

function categoryOf(key: string): string {
  for (const c of CATEGORIES) if (c.match.test(key)) return c.name;
  return 'Other';
}

const COMMON_FACTS: { key: string; label: string }[] = [
  { key: 'business_model',  label: 'Business model' },
  { key: 'headcount',       label: 'Headcount' },
  { key: 'industry',        label: 'Industry' },
  { key: 'products',        label: 'Products / services' },
  { key: 'office_address',  label: 'Office address' },
  { key: 'service_area',    label: 'Service area' },
  { key: 'owner',           label: 'Owner' },
  { key: 'decision_maker',  label: 'Decision-maker' },
  { key: 'primary_system',  label: 'Primary system' },
  { key: 'annual_revenue',  label: 'Annual revenue' },
  { key: 'website',         label: 'Website' },
  { key: 'years_in_business', label: 'Years in business' },
];

function labelize(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function prettySource(source?: string | null): { icon: string; text: string } {
  if (!source) return { icon: '✎', text: 'manual' };
  const [kind, rest] = source.includes(':') ? [source.slice(0, source.indexOf(':')), source.slice(source.indexOf(':') + 1)] : [source, ''];
  const k = kind.toLowerCase();
  const icon = k === 'meeting' ? '🗣' : k === 'website' ? '🌐' : k === 'linkedin' ? '💼' : k === 'email' ? '✉' : k === 'phone' || k === 'call' ? '📞' : '✎';
  if (rest && /^\d{4}-\d{2}-\d{2}$/.test(rest)) {
    const d = new Date(rest + 'T00:00:00');
    const formatted = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return { icon, text: `${kind} · ${formatted}` };
  }
  return { icon, text: source };
}

function relativeAge(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  const day = 86_400_000;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  if (diff < 30 * day) return `${Math.floor(diff / day)}d ago`;
  if (diff < 365 * day) return `${Math.floor(diff / (30 * day))}mo ago`;
  return `${Math.floor(diff / (365 * day))}y ago`;
}

// @section 3 FactsSection — card-grid grouped by category + quick-add chips
function FactsSection({ companyId, deals }: { companyId: string; deals: AccountDeal[] }) {
  const [items, setItems] = useState<BdFact[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [composerKey, setComposerKey] = useState<string | null>(null);

  const reload = () => listBdFacts(companyId).then(setItems).catch((e) => setErr(String(e)));
  useEffect(() => { reload(); }, [companyId]);

  const grouped = useMemo(() => {
    const m = new Map<string, BdFact[]>();
    for (const f of items ?? []) {
      const cat = categoryOf(f.key);
      if (!m.has(cat)) m.set(cat, []);
      m.get(cat)!.push(f);
    }
    const order = [...CATEGORIES.map((c) => c.name), 'Other'];
    return order
      .filter((name) => m.has(name))
      .map((name) => [name, m.get(name)!] as const);
  }, [items]);

  const capturedKeys = useMemo(() => new Set((items ?? []).map((f) => f.key.toLowerCase())), [items]);
  const quickAdd = COMMON_FACTS.filter((c) => !capturedKeys.has(c.key));

  return (
    <Card title="Facts" subtitle={items ? `${items.length} captured · click any card to edit` : 'Loading…'} err={err}>
      {items !== null && (
        <>
          {quickAdd.length > 0 && deals.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: '#6b7a90', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>
                Capture next
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {quickAdd.slice(0, 8).map((c) => (
                  <button
                    key={c.key}
                    onClick={() => setComposerKey(c.key)}
                    style={quickAddChip}
                  >
                    + {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {deals.length > 0 && (
            composerKey === null ? (
              <AddFactTile onClick={() => setComposerKey('')} />
            ) : (
              <AddFactComposer
                companyId={companyId}
                deals={deals}
                initialKey={composerKey}
                onSaved={() => { setComposerKey(null); reload(); }}
                onCancel={() => setComposerKey(null)}
              />
            )
          )}

          {items.length === 0 ? (
            <Empty>No facts captured yet. Use a chip above or click "+ Add fact" to log the first one.</Empty>
          ) : (
            <div style={{ display: 'grid', gap: 16, marginTop: 14 }}>
              {grouped.map(([category, facts]) => (
                <div key={category}>
                  <div style={categoryHeader}>
                    <span>{category}</span>
                    <span style={{ color: '#6b7a90' }}>{facts.length}</span>
                  </div>
                  <div style={factGrid}>
                    {facts.map((f) => <FactCard key={f.id} fact={f} onUpdated={reload} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// @section 4 FactCard — click to edit value / source in place
function FactCard({ fact, onUpdated }: { fact: BdFact; onUpdated: () => void }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <FactCardEditor
        fact={fact}
        onSaved={() => { setEditing(false); onUpdated(); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const src = prettySource(fact.source);
  const age = relativeAge(fact.updatedAt);

  return (
    <div
      style={{ ...factCardStyle, cursor: 'pointer' }}
      onClick={() => setEditing(true)}
      title="Click to edit"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true); } }}
    >
      <div style={factLabelStyle}>{labelize(fact.key)}</div>
      <div style={factValueStyle}>{fact.value}</div>
      <div style={factMetaStyle}>
        <span title={fact.source ?? 'manual entry'}>{src.icon} {src.text}</span>
        {fact.sourceUrl && (
          <>
            {' · '}
            <a href={fact.sourceUrl} target="_blank" rel="noreferrer" style={{ color: '#7eb6e8' }} onClick={(e) => e.stopPropagation()}>
              open
            </a>
          </>
        )}
        <span style={{ marginLeft: 'auto', color: '#6b7a90' }}>{age}</span>
      </div>
    </div>
  );
}

function FactCardEditor({ fact, onSaved, onCancel }: {
  fact: BdFact; onSaved: () => void; onCancel: () => void;
}) {
  const [value, setValue] = useState(fact.value);
  const [source, setSource] = useState(fact.source ?? '');
  const [sourceUrl, setSourceUrl] = useState(fact.sourceUrl ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const dirty = value !== fact.value || source !== (fact.source ?? '') || sourceUrl !== (fact.sourceUrl ?? '');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await patchBdFact(fact.id, {
        value: value.trim(),
        source: source.trim(),
        sourceUrl: sourceUrl.trim(),
      });
      onSaved();
    } catch (ex) {
      setErr(String(ex));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      style={{
        ...factCardStyle,
        border: '1px solid #7eb6e8',
        background: '#1d2330',
        gap: 6,
      }}
    >
      <div style={factLabelStyle}>{labelize(fact.key)}</div>
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="value"
        style={inlineEditInputBig}
      />
      <input
        value={source}
        onChange={(e) => setSource(e.target.value)}
        placeholder="source (e.g. meeting:2026-05-27, website, linkedin)"
        style={inlineEditInputSmall}
      />
      <input
        value={sourceUrl}
        onChange={(e) => setSourceUrl(e.target.value)}
        placeholder="source URL (optional)"
        style={inlineEditInputSmall}
      />
      {err && <div style={{ color: '#e87e7e', fontSize: 11 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <button type="submit" disabled={busy || !value.trim() || !dirty}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} style={cancelBtnStyle}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function AddFactTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        padding: '10px 14px',
        border: '1px dashed #2a3346',
        borderRadius: 6,
        background: 'transparent',
        color: '#a3b1c2',
        fontSize: 13,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ fontSize: 16, color: '#7eb6e8' }}>+</span>
      <span>Add a custom fact</span>
      <span style={{ fontSize: 11, color: '#6b7a90', marginLeft: 'auto' }}>
        or click a chip above for a common one
      </span>
    </button>
  );
}

function AddFactComposer({ companyId, deals, initialKey, onSaved, onCancel }: {
  companyId: string; deals: AccountDeal[]; initialKey: string;
  onSaved: () => void; onCancel: () => void;
}) {
  const [dealId, setDealId] = useState(deals[0].id);
  const [key, setKey] = useState(initialKey);
  const [value, setValue] = useState('');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!key.trim() || !value.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await createBdFact({
        companyId, dealId,
        key: key.trim(),
        value: value.trim(),
        source: source.trim() || undefined,
      });
      onSaved();
    } catch (ex) {
      setErr(String(ex));
    } finally {
      setBusy(false);
    }
  }

  const previewLabel = key.trim() ? labelize(key.trim()) : 'New fact';

  return (
    <form
      onSubmit={submit}
      style={{
        padding: '12px 14px',
        border: '1px solid #7eb6e8',
        borderRadius: 6,
        background: '#1d2330',
        display: 'grid',
        gap: 8,
      }}
    >
      <div style={factLabelStyle}>{previewLabel}</div>
      <input
        autoFocus={!initialKey}
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="key — e.g. business_model, headcount, owner"
        style={inlineEditInputSmall}
      />
      <input
        autoFocus={!!initialKey}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="value"
        style={inlineEditInputBig}
      />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <DealPicker deals={deals} value={dealId} onChange={setDealId} />
        <input
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="source (optional, e.g. meeting:2026-05-27)"
          style={{ ...inlineEditInputSmall, flex: 1, minWidth: 200 }}
        />
      </div>
      {err && <div style={{ color: '#e87e7e', fontSize: 11 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="submit" disabled={busy || !key.trim() || !value.trim()}>
          {busy ? 'Adding…' : 'Add fact'}
        </button>
        <button type="button" onClick={onCancel} style={cancelBtnStyle}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// @section 5 Shared local helpers
function NoDealsBanner() {
  return (
    <div style={{ ...cardStyle, borderStyle: 'dashed' }}>
      <div style={{ fontSize: 13, color: '#a3b1c2' }}>
        This account has no deals yet. Facts attach to a specific deal — create one in the Overview lens first.
      </div>
    </div>
  );
}

function Card({ title, subtitle, err, children }: { title: string; subtitle?: string; err?: string | null; children: React.ReactNode }) {
  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1.5, color: '#a3b1c2' }}>{title}</h3>
        {subtitle && <span style={{ fontSize: 11, color: '#6b7a90' }}>{subtitle}</span>}
      </div>
      {err && <div style={{ color: '#e87e7e', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <em style={{ color: '#6b7a90', fontSize: 13 }}>{children}</em>;
}

function DealPicker({ deals, value, onChange }: { deals: AccountDeal[]; value: string; onChange: (v: string) => void }) {
  if (deals.length === 1) {
    return <span style={{ fontSize: 11, color: '#6b7a90' }}>deal: {deals[0].name}</span>;
  }
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {deals.map((d) => (
        <option key={d.id} value={d.id}>{d.name}{d.closedAt ? ' (closed)' : ''}</option>
      ))}
    </select>
  );
}

const cardStyle: CSSProperties = {
  border: '1px solid #232a37',
  borderRadius: 8,
  padding: 14,
  background: '#171c26',
};
const inlineEditInputBig: CSSProperties = {
  fontSize: 14,
  padding: '6px 8px',
  background: '#171c26',
  border: '1px solid #2a3346',
  borderRadius: 4,
  color: '#e6e8ee',
  fontFamily: 'inherit',
};
const inlineEditInputSmall: CSSProperties = {
  fontSize: 12,
  padding: '5px 8px',
  background: '#171c26',
  border: '1px solid #2a3346',
  borderRadius: 4,
  color: '#a3b1c2',
  fontFamily: 'inherit',
};
const quickAddChip: CSSProperties = {
  fontSize: 11,
  padding: '4px 10px',
  border: '1px dashed #2a3346',
  borderRadius: 99,
  background: 'transparent',
  color: '#a3b1c2',
  cursor: 'pointer',
};
const categoryHeader: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: 2,
  color: '#a3b1c2',
  paddingBottom: 6,
  marginBottom: 8,
  borderBottom: '1px solid #232a37',
};
const factGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 8,
};
const factCardStyle: CSSProperties = {
  padding: '10px 12px',
  border: '1px solid #232a37',
  borderRadius: 6,
  background: '#1d2330',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};
const factLabelStyle: CSSProperties = {
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: 1.5,
  color: '#7eb6e8',
  fontWeight: 600,
};
const factValueStyle: CSSProperties = {
  fontSize: 14,
  color: '#e6e8ee',
  lineHeight: 1.4,
  fontWeight: 500,
};
const factMetaStyle: CSSProperties = {
  fontSize: 10,
  color: '#6b7a90',
  marginTop: 4,
  display: 'flex',
  gap: 4,
  alignItems: 'center',
};
const cancelBtnStyle: CSSProperties = {
  fontSize: 11,
  padding: '3px 8px',
  border: '1px solid #2a3346',
  borderRadius: 4,
  background: 'transparent',
  color: '#a3b1c2',
  cursor: 'pointer',
};
